import {
    C_SCORES_DATE_FORMAT,
    MOMENTJS_DATE_DISPLAY_FORMAT,
} from '../../../constants';
import _ from 'lodash';
import moment from 'moment';
import {
    E_CHART_GROUP_IDENTIFIERS,
    IChartData,
    IChartLine,
} from 'components/GQChart/chartInterfaces';
import {
    E_RISK_TYPE,
    GLOBAL_INDEXES,
    IExportChartConfig,
} from '../../../services/lib/APIServiceInterfaces';

import {
    getGlobalIndexColor,
    getGlobalIndexLabel,
    isContingentMode,
    isPulseChart,
    isRiskChart,
    isSingleCountryMode,
    isSortMode,
} from '../templateUtils';
import ExportChartChip from './ExportChartChip';
import { FGTypography } from '@fg/design-system';
import { ICircleData, VIEW } from 'interfaces';
import { IExportChartData, IPreloadedMapApiData } from '../exportInterfaces';
import { getRiskLongName, RISK_TYPE_TO_LABEL } from 'utils/riskUtils';

export const DATA_KEYS = {
    [E_CHART_GROUP_IDENTIFIERS.PROJECTION_LINE]: 'RiskScoreProject',
};

const CHART_GROUP_IDENTIFIERS_TO_LABELS = {
    [E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK]: 'Composite',
    [E_CHART_GROUP_IDENTIFIERS.PULSE]: 'Pulse',
    [E_CHART_GROUP_IDENTIFIERS.PROJECTION_LINE]: 'Past Projections',
    [E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE]: 'Currency',
};

const formatCurrency = (value: number) =>
    value < 1000 ? value.toFixed(2) : (value / 1000).toFixed(1) + 'K';

const getDimensionChips = (chartConfig: IExportChartConfig) =>
    chartConfig.riskIds.map(d => {
        const colorStack =
            chartConfig.riskType === E_RISK_TYPE.DIMENSIONS ||
            chartConfig.riskType === E_RISK_TYPE.CONTINGENT
                ? chartConfig.colorsStack.dimensions
                : chartConfig.colorsStack.clientFacingIndicators;
        const displayName = getRiskLongName(chartConfig.riskType, d);
        const riskStackItem = _.find(colorStack, item => item.key === d);
        const useColor = !isContingentMode(chartConfig);

        return {
            label: displayName,
            ...(useColor ? { color: riskStackItem.color } : {}),
        };
    });

const getRiskValueChips = (chartData: IExportChartData, dateKey: string) =>
    (chartData as IChartData[])
        .find(c => c.identifier === E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK)
        ?.lines.filter(
            line =>
                !line.identifier.startsWith(
                    DATA_KEYS[E_CHART_GROUP_IDENTIFIERS.PROJECTION_LINE]
                )
        )
        .map((line: IChartLine) => {
            return { label: line.getValue(dateKey)?.yValue.toFixed(1) };
        });

const getCRRiskValueChips = (chartData: IExportChartData, dateKey: string) =>
    (chartData as IChartData[])
        .find(c => c.identifier === E_CHART_GROUP_IDENTIFIERS.CONTINGENT_RISK)
        ?.lines.map((line: IChartLine) => ({
            label: line.getValue(dateKey)?.yValue.toFixed(1),
        }));

const getPulseValueChips = (chartData: IExportChartData, dateKey: string) =>
    (chartData as IChartData[])
        .find(c => c.identifier === E_CHART_GROUP_IDENTIFIERS.PULSE)
        ?.lines.map((line: IChartLine) => ({
            label: line.getValue(dateKey)?.yValue.toFixed(3),
        }));

const getPastProjectionValueChips = (
    chartData: IExportChartData,
    dateKey: string
) =>
    (chartData as IChartData[])
        .find(c => c.identifier === E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK)
        ?.lines.filter(line =>
            line.identifier.startsWith(
                DATA_KEYS[E_CHART_GROUP_IDENTIFIERS.PROJECTION_LINE]
            )
        )
        .map((line: IChartLine) => ({
            label: line.getValue(dateKey)?.yValue.toFixed(3),
        }));

const getCurrencyValueChips = (chartData: IExportChartData, dateKey: string) =>
    (chartData as IChartData[])
        .find(c => c.identifier === E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE)
        ?.lines.map((line: IChartLine) => ({
            label: formatCurrency(line.getValue(dateKey)?.yValue),
        }));

const getGlobalIndexValueChips = (
    chartConfig: IExportChartConfig,
    chartData: IExportChartData,
    dateKey: string
) => {
    let value;
    const lineData = (chartData as IChartData[]).find(
        c => c.identifier === E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX
    )?.lines[0];
    const baseVal = lineData.getValue(dateKey)?.yValue;
    const globalIndexType = lineData.extraData.indicator as GLOBAL_INDEXES;
    if (globalIndexType === 'oil_price' || globalIndexType === 'gold_price') {
        value = formatCurrency(baseVal);
    } else if (globalIndexType === 'real_gdp_growth') {
        value = baseVal.toFixed(2);
    } else if (globalIndexType === 'cpi_inflation') {
        value = `${baseVal.toFixed(3)}%`;
    }
    const globalIndexColor = getGlobalIndexColor(chartConfig);
    return [{ color: globalIndexColor, label: value }];
};

const getCountryChips = (chartConfig, countryIds: number[]) =>
    countryIds.map((c, i) => {
        const countryColorStack = _.find(
            chartConfig.colorsStack.countries,
            stackItem => stackItem.key === c
        );
        const countryDisplayData = chartConfig.displayNames.countries[c];

        return {
            color: countryColorStack.color,
            label: countryDisplayData.name,
        };
    });

const getSortByValueChips = (
    chartData: IExportChartData,
    selectedCountryIds: number[]
) =>
    selectedCountryIds.map((cId: number) => {
        const countryScore = (chartData as IPreloadedMapApiData).scoreDeltas.find(
            (score: ICircleData) => score.geo.id === cId
        );
        return { label: countryScore.score_today };
    });

interface Props {
    chartConfig: IExportChartConfig;
    chartData: IExportChartData;
}
const ExportChartFooter = ({ chartConfig, chartData }: Props) => {
    const getFooterColumns = (): Array<{ name: string; values: any[] }> => {
        if (chartConfig.viewType === VIEW.HEATMAP) {
            return [];
        }
        const footerColumns = [];

         if (isSortMode(chartConfig)) {
            footerColumns.push({
                name: 'Risk type',
                values: [{ label: RISK_TYPE_TO_LABEL[chartConfig.riskType] }],
            });

            const selectedCountryIds = isContingentMode(chartConfig)
                ? chartConfig.countries.slice(1)
                : chartConfig.countries;

            footerColumns.push({
                name: `Countr${selectedCountryIds.length > 1 ? 'ies' : 'y'}`,
                values: getCountryChips(chartConfig, selectedCountryIds),
            });

            footerColumns.push({
                name:
                    CHART_GROUP_IDENTIFIERS_TO_LABELS[
                        E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK
                    ],
                values: getSortByValueChips(chartData, selectedCountryIds),
            });
            return footerColumns;
        }

        let dateKey = moment(chartConfig.virtualToday).format(
            C_SCORES_DATE_FORMAT
        );

        footerColumns.push({
            name: 'Selected date',
            values: [
                {
                    label: moment(chartConfig.virtualToday).format(
                        MOMENTJS_DATE_DISPLAY_FORMAT
                    ),
                },
            ],
        });
        
        footerColumns.push({
            name: 'Risk type',
            values: [{ label: RISK_TYPE_TO_LABEL[chartConfig.riskType] }],
        });

        if (!isSingleCountryMode(chartConfig)) {
            const countryIds = isContingentMode(chartConfig)
                ? chartConfig.countries.slice(1)
                : chartConfig.countries;
            footerColumns.push({
                name: `Countr${countryIds.length > 1 ? 'ies' : 'y'}`,
                values: getCountryChips(chartConfig, countryIds),
            });
        }

        else {
            footerColumns.push({
                name: `Risk name${chartConfig.riskIds.length > 1 ? 's' : ''}`,
                values: getDimensionChips(chartConfig),
            });
        }
            if (isContingentMode(chartConfig) && isRiskChart(chartConfig.riskChartMode)) {
                footerColumns.push({
                    name:
                        CHART_GROUP_IDENTIFIERS_TO_LABELS[
                            E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK
                        ],
                    values: getCRRiskValueChips(chartData, dateKey),
                });
            } else if (isRiskChart(chartConfig.riskChartMode)) {
                footerColumns.push({
                    name:
                        CHART_GROUP_IDENTIFIERS_TO_LABELS[
                            E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK
                        ],
                    values: getRiskValueChips(chartData, dateKey),
                });
            }
            if (isPulseChart(chartConfig.riskChartMode)) {
                footerColumns.push({
                    name:
                        CHART_GROUP_IDENTIFIERS_TO_LABELS[
                            E_CHART_GROUP_IDENTIFIERS.PULSE
                        ],
                    values: getPulseValueChips(chartData, dateKey),
                });
            }

            if (chartConfig.projectionDataOn) {
                footerColumns.push({
                    name:
                        CHART_GROUP_IDENTIFIERS_TO_LABELS[
                            E_CHART_GROUP_IDENTIFIERS.PROJECTION_LINE
                        ],
                    values: getPastProjectionValueChips(chartData, dateKey),
                });
            }

            if (chartConfig.currencyOn) {
                footerColumns.push({
                    name:
                        CHART_GROUP_IDENTIFIERS_TO_LABELS[
                            E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE
                        ],
                    values: getCurrencyValueChips(chartData, dateKey),
                });
            } else if (chartConfig.globalIndexTypes.length > 0) {
                const globalIndexLabel = getGlobalIndexLabel(chartConfig);
                footerColumns.push({
                    name: globalIndexLabel,
                    values: getGlobalIndexValueChips(
                        chartConfig,
                        chartData,
                        dateKey
                    ),
                });
            }
        return footerColumns;
    };

    return (
        <div className="export-chart-footer">
            {getFooterColumns().map(({ name, values }) => (
                <div
                    key={name}
                    style={{
                        marginRight: '20px',
                    }}>
                    <FGTypography tag="label" variant="label3">
                        {name}
                    </FGTypography>
                    {values.map(v => (
                        <ExportChartChip color={v.color} label={v.label} key={v.label}/>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default ExportChartFooter;
